import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  MenuItem,
  Menu,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Modal,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState, useCallback } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { ViewDocumentModal } from "../../components/modals/ViewDocumentModal";
import { KYCStatusModal } from "../../components/modals/KYCStatusModal";
import WarningModal from "../../components/modals/WarningModal";
import RejectRequestModal from "../../components/modals/RejectRequestModal";
import useAuth from "../../hooks/useAuth";

const ManageKYC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [generalKycData, setGeneralKycData] = useState([]);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalFilter, setGeneralFilter] = useState("UNDER_REVIEW");
  const [createUserAnchorEl, setCreateUserAnchorEl] = useState(null);
  const createUserOpen = Boolean(createUserAnchorEl);

  // NIN State
  const [ninKycData, setNinKycData] = useState([]);
  const [ninLoading, setNinLoading] = useState(false);
  const [ninFilter, setNinFilter] = useState("PENDING");
  const [ninFilterAnchorEl, setNinFilterAnchorEl] = useState(null);
  const ninFilterOpen = Boolean(ninFilterAnchorEl);
  const [selectedNinRow, setSelectedNinRow] = useState(null);
  const [ninDetails, setNinDetails] = useState(null);
  const [loadingNinDetails, setLoadingNinDetails] = useState(false);
  const [ninModalOpen, setNinModalOpen] = useState(false);
  const [ninRemarks, setNinRemarks] = useState("");

  // Utility Bill State
  const [utilityKycData, setUtilityKycData] = useState([]);
  const [utilityLoading, setUtilityLoading] = useState(false);
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [utilityModalOpen, setUtilityModalOpen] = useState(false);
  const [utilityRemarks, setUtilityRemarks] = useState("");

  // CAC State
  const [cacKycData, setCacKycData] = useState([]);
  const [cacLoading, setCacLoading] = useState(false);
  const [selectedCac, setSelectedCac] = useState(null);
  const [cacModalOpen, setCacModalOpen] = useState(false);
  const [cacRemarks, setCacRemarks] = useState("");
  const [cacApprovedBy, setCacApprovedBy] = useState("");

  // Existing Modal states
  const [modal, setModal] = useState({ open: false, id: null });
  const [docModal, setDocModal] = useState({ open: false, imgUrl: "" });
  const [warning, setWarning] = useState({
    openConfirm: false,
    url: "",
    user: {},
    action: "",
  });
  const [reject, setReject] = useState({
    open: false,
    url: "",
    user: {},
    action: "",
  });
  const [viewDoc, setViewDoc] = useState({
    open: false,
    imgUrl: "",
  });

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);
  const cardBg = theme.palette.mode === "dark" ? colors.primary[500] : "#ffffff";
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError, cookies } = useAuth();

  // Populate CAC admin email default
  useEffect(() => {
    if (cookies?.auth?.user?.sub || cookies?.auth?.user?.email) {
      setCacApprovedBy(cookies.auth.user.sub || cookies.auth.user.email);
    }
  }, [cookies]);

  // Fetch functions
  const fetchGeneralKYC = useCallback(async (status = "UNDER_REVIEW") => {
    setGeneralLoading(true);
    try {
      const response = await axiosPrivate.get(
        "/audit-reporting/admin/customers/kyc",
        {
          params: {
            pageNo: 1,
            pageSize: 10000,
            status: status,
            field: "status",
            value: status,
          },
        }
      );
      const data = response?.data;
      let rawList = [];
      if (Array.isArray(data)) {
        rawList = data;
      } else if (Array.isArray(data?.data?.content)) {
        rawList = data.data.content;
      } else if (Array.isArray(data?.data)) {
        rawList = data.data;
      } else if (Array.isArray(data?.content)) {
        rawList = data.content;
      }

      let list = [];
      rawList.forEach((user, userIdx) => {
        const userId = user.userId;
        const accounts = user.userAccounts;
        if (!accounts || accounts.length === 0) {
          list.push({
            id: `user-${userId}-${userIdx}`,
            userId,
            accountNumber: "N/A",
            accountName: "N/A",
            accountType: "N/A",
            kycStatus: "N/A",
            currentTierDisplayName: "N/A",
            currentTierLevel: "N/A",
            progressPercentage: 0,
            ninVerificationStatus: "N/A",
            bvnVerificationStatus: "N/A",
            faceVerificationStatus: "N/A",
            utilityBillVerificationStatus: "N/A",
            addressVerificationStatus: "N/A",
            cacCertificateVerificationStatus: "N/A",
          });
        } else {
          accounts.forEach((acc, accIdx) => {
            list.push({
              ...acc,
              id: `acc-${acc.accountId || acc.accountNumber || userIdx}-${accIdx}`,
              userId,
            });
          });
        }
      });
      setGeneralKycData(list);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch general KYC data");
    } finally {
      setGeneralLoading(false);
    }
  }, [axiosPrivate, snackError]);

  const fetchNINKYC = useCallback(async () => {
    setNinLoading(true);
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/pending-nin-verifications"
      );
      const data = response?.data;
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.data)) {
        list = data.data;
      } else if (Array.isArray(data?.content)) {
        list = data.content;
      }
      setNinKycData(list);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch NIN verifications");
    } finally {
      setNinLoading(false);
    }
  }, [axiosPrivate, snackError]);

  const fetchUtilityKYC = useCallback(async () => {
    setUtilityLoading(true);
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/utility-bill-verifications/pending"
      );
      const data = response?.data;
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.data)) {
        list = data.data;
      } else if (Array.isArray(data?.content)) {
        list = data.content;
      }
      setUtilityKycData(list);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch Utility Bill verifications");
    } finally {
      setUtilityLoading(false);
    }
  }, [axiosPrivate, snackError]);

  const fetchCacKYC = useCallback(async () => {
    setCacLoading(true);
    try {
      const response = await axiosPrivate.get(
        "/auth-serviceV3/api/v1/cac-business-accounts/pending-approval"
      );
      const data = response?.data;
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.data)) {
        list = data.data;
      } else if (Array.isArray(data?.content)) {
        list = data.content;
      }
      setCacKycData(list);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch CAC accounts pending approval");
    } finally {
      setCacLoading(false);
    }
  }, [axiosPrivate, snackError]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchGeneralKYC(generalFilter);
    } else if (activeTab === 1) {
      fetchNINKYC();
    } else if (activeTab === 2) {
      fetchUtilityKYC();
    } else if (activeTab === 3) {
      fetchCacKYC();
    }
  }, [activeTab, generalFilter, fetchGeneralKYC, fetchNINKYC, fetchUtilityKYC, fetchCacKYC]);

  const toggleCreateUserMenu = (event) => {
    setCreateUserAnchorEl(event?.currentTarget);
  };

  const handleCreateUserClose = () => {
    setCreateUserAnchorEl(null);
  };

  const handleOption = (e) => {
    setCreateUserAnchorEl(null);
    const text = e.target.textContent;
    if (text === "UNDER REVIEW") {
      setGeneralFilter("UNDER_REVIEW");
    } else if (text === "PENDING") {
      setGeneralFilter("PENDING");
    } else if (text === "REJECTED") {
      setGeneralFilter("REJECTED");
    }
  };

  // NIN Details view trigger
  const handleOpenNinDetails = async (row) => {
    setSelectedNinRow(row);
    setNinModalOpen(true);
    setLoadingNinDetails(true);
    setNinRemarks("");
    setNinDetails(null);
    try {
      const response = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/pending-nin-verifications/verify-virtual-nin?userId=${row.userId}`
      );
      setNinDetails(response?.data?.data);
    } catch (error) {
      console.error(error);
      snackError("Failed to fetch virtual NIN details");
    } finally {
      setLoadingNinDetails(false);
    }
  };

  // NIN Actions
  const handleNinAction = async (approved) => {
    if (!selectedNinRow) return;
    const actionPath = approved ? "approve" : "reject";
    try {
      const response = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/pending-nin-verifications/${selectedNinRow.id}/${actionPath}`,
        null,
        {
          params: {
            remarks: ninRemarks,
          },
        }
      );
      success(response?.data?.message || `NIN verification successfully ${actionPath}d`);
      setNinModalOpen(false);
      fetchNINKYC();
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || `Failed to ${actionPath} NIN verification`);
    }
  };

  // Utility Actions
  const handleUtilityAction = async (approved) => {
    if (!selectedUtility) return;
    const actionPath = approved ? "approve" : "reject";
    try {
      const response = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/utility-bill-verifications/${selectedUtility.id}/${actionPath}`,
        null,
        {
          params: {
            remarks: utilityRemarks,
          },
        }
      );
      success(response?.data?.message || `Utility bill successfully ${actionPath}d`);
      setUtilityModalOpen(false);
      fetchUtilityKYC();
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || `Failed to ${actionPath} Utility bill`);
    }
  };

  // CAC Actions
  const handleCacAction = async (approved) => {
    if (!selectedCac) return;
    if (approved && !cacApprovedBy.trim()) {
      snackError("Please input an admin email address for CAC approval");
      return;
    }
    const actionPath = approved ? "approve" : "reject";
    try {
      const params = approved ? { approvedBy: cacApprovedBy } : { reason: cacRemarks };
      const response = await axiosPrivate.post(
        `/auth-serviceV3/api/v1/cac-business-accounts/${selectedCac.id}/${actionPath}`,
        null,
        { params }
      );
      success(response?.data?.message || `CAC account successfully ${actionPath}d`);
      setCacModalOpen(false);
      fetchCacKYC();
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || `Failed to ${actionPath} CAC account`);
    }
  };

  // Helper to render verification status elegantly
  const renderVerificationStatus = (status) => {
    if (!status) return <Typography color={colors.grey[300]} variant="body2">N/A</Typography>;
    let color = colors.grey[300];
    const upper = status.toUpperCase();
    if (upper === "VERIFIED" || upper === "APPROVED" || upper === "SUCCESS") {
      color = colors.greenAccent[500];
    } else if (upper === "PENDING" || upper === "UNDER_REVIEW") {
      color = colors.orangeAccent[500];
    } else if (upper === "REJECTED" || upper === "FAILED" || upper === "DECLINED") {
      color = colors.redAccent[500];
    }
    return (
      <Typography color={color} variant="body2" fontWeight="bold">
        {status}
      </Typography>
    );
  };

  // General Columns
  const columnsGeneral = [
    { field: "userId", headerName: "User ID", width: 80 },
    { field: "accountName", headerName: "Account Name", width: 180 },
    { field: "accountNumber", headerName: "Account Number", width: 120 },
    { field: "accountType", headerName: "Account Type", width: 110 },
    { field: "currentTierDisplayName", headerName: "Tier Level", width: 150 },
    {
      field: "kycStatus",
      headerName: "KYC Status",
      width: 110,
      renderCell: ({ row: { kycStatus } }) => renderVerificationStatus(kycStatus)
    },
    {
      field: "progressPercentage",
      headerName: "Progress",
      width: 140,
      renderCell: ({ row: { progressPercentage } }) => {
        const pct = progressPercentage || 0;
        return (
          <Box display="flex" alignItems="center" width="100%" gap={1}>
            <Box width="100%">
              <LinearProgress
                variant="determinate"
                value={pct}
                color="secondary"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
            <Typography variant="body2" color={colors.grey[100]} sx={{ minWidth: "30px" }}>
              {pct}%
            </Typography>
          </Box>
        );
      }
    },
    {
      field: "ninVerificationStatus",
      headerName: "NIN Status",
      width: 110,
      renderCell: ({ row: { ninVerificationStatus } }) => renderVerificationStatus(ninVerificationStatus)
    },
    {
      field: "bvnVerificationStatus",
      headerName: "BVN Status",
      width: 110,
      renderCell: ({ row: { bvnVerificationStatus } }) => renderVerificationStatus(bvnVerificationStatus)
    },
    {
      field: "utilityBillVerificationStatus",
      headerName: "Utility Status",
      width: 110,
      renderCell: ({ row: { utilityBillVerificationStatus } }) => renderVerificationStatus(utilityBillVerificationStatus)
    },
    {
      field: "faceVerificationStatus",
      headerName: "Face Status",
      width: 110,
      renderCell: ({ row: { faceVerificationStatus } }) => renderVerificationStatus(faceVerificationStatus)
    },
    {
      field: "cacCertificateVerificationStatus",
      headerName: "CAC Status",
      width: 110,
      renderCell: ({ row: { cacCertificateVerificationStatus } }) => renderVerificationStatus(cacCertificateVerificationStatus)
    }
  ];

  // NIN Columns
  const columnsNIN = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "userId", headerName: "User ID", width: 80 },
    { field: "email", headerName: "Email", width: 220 },
    { field: "nin", headerName: "NIN", width: 140 },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 180,
      valueGetter: (params) => `${params.row.firstName || ""} ${params.row.lastName || ""}`.trim() || "N/A"
    },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "createdAt",
      headerName: "Date Created",
      width: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : "N/A"
    },
    {
      field: "action",
      headerName: "Actions",
      width: 150,
      renderCell: ({ row }) => (
        <Button
          onClick={() => handleOpenNinDetails(row)}
          variant="contained"
          size="small"
          startIcon={<VisibilityIcon />}
          sx={{
            backgroundColor: colors.orangeAccent[700],
            color: colors.grey[100],
            "&:hover": { backgroundColor: colors.orangeAccent[600] }
          }}
        >
          Compare
        </Button>
      )
    }
  ];

  // Utility Bill Columns
  const columnsUtility = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "userId", headerName: "User ID", width: 100 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "createdAt",
      headerName: "Date Created",
      width: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : "N/A"
    },
    {
      field: "documentUrl",
      headerName: "Document",
      width: 150,
      renderCell: ({ row }) => (
        <Button
          onClick={() => {
            setSelectedUtility(row);
            setUtilityRemarks("");
            setUtilityModalOpen(true);
          }}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: colors.orangeAccent[700],
            color: colors.grey[100],
          }}
        >
          Verify
        </Button>
      )
    }
  ];

  // CAC Columns
  const columnsCAC = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "businessName", headerName: "Business Name", width: 200 },
    { field: "rcNumber", headerName: "RC Number", width: 120 },
    { field: "email", headerName: "Email", width: 180 },
    { field: "phoneNo", headerName: "Phone Number", width: 140 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "createdAt",
      headerName: "Date Created",
      width: 180,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : "N/A"
    },
    {
      field: "action",
      headerName: "Actions",
      width: 130,
      renderCell: ({ row }) => (
        <Button
          onClick={() => {
            setSelectedCac(row);
            setCacRemarks("");
            setCacModalOpen(true);
          }}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: colors.orangeAccent[700],
            color: colors.grey[100],
          }}
        >
          Review
        </Button>
      )
    }
  ];

  // Client-side NIN status filter
  const filteredNinData = ninKycData.filter((item) => {
    if (ninFilter === "ALL") return true;
    return item.status === ninFilter;
  });

  return (
    <Box m="20px">
      <ViewDocumentModal modal={viewDoc} setModal={setViewDoc} />
      <ViewDocumentModal modal={docModal} setModal={setDocModal} />
      <WarningModal
        open={warning.openConfirm}
        url={warning.url}
        user={warning.user}
        setOpen={setWarning}
        action={warning.action}
        reload={() => fetchGeneralKYC(generalFilter)}
      />
      <RejectRequestModal
        isOpen={reject.open}
        setIsOpen={setReject}
        url={reject.url}
        user={reject.user}
        action={reject.action}
        reload={() => fetchGeneralKYC(generalFilter)}
      />
      <KYCStatusModal
        modal={modal}
        setModal={setModal}
        setDocModal={setDocModal}
      />

      {/* HEADER */}
      <Box
        display={smScreen ? "flex" : "block"}
        flexDirection={smScreen ? "row" : "column"}
        justifyContent={smScreen ? "space-between" : "start"}
        alignItems={smScreen ? "center" : "start"}
        m="10px 0"
      >
        <Header title="KYC Management" subtitle="Manage WayaBank Customer Verification Workflows" />

        {activeTab === 0 && (
          <Box display={"flex"} justifyContent={"space-between"}>
            <Box>
              <Button
                onClick={toggleCreateUserMenu}
                underline="none"
                sx={{
                  backgroundColor: colors.orangeAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 38px",
                  marginLeft: "15px",
                }}
              >
                Filter: {generalFilter.replace("_", " ")}
              </Button>

              <Menu
                id="basic-menu"
                anchorEl={createUserAnchorEl}
                open={createUserOpen}
                onClose={handleCreateUserClose}
              >
                <MenuItem onClick={handleOption}>UNDER REVIEW</MenuItem>
                <MenuItem onClick={handleOption}>PENDING</MenuItem>
                <MenuItem onClick={handleOption}>REJECTED</MenuItem>
              </Menu>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box display={"flex"} justifyContent={"space-between"}>
            <Box>
              <Button
                onClick={(e) => setNinFilterAnchorEl(e.currentTarget)}
                underline="none"
                sx={{
                  backgroundColor: colors.orangeAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 38px",
                  marginLeft: "15px",
                }}
              >
                Filter: {ninFilter}
              </Button>

              <Menu
                id="nin-filter-menu"
                anchorEl={ninFilterAnchorEl}
                open={ninFilterOpen}
                onClose={() => setNinFilterAnchorEl(null)}
              >
                <MenuItem onClick={() => { setNinFilter("PENDING"); setNinFilterAnchorEl(null); }}>PENDING</MenuItem>
                <MenuItem onClick={() => { setNinFilter("APPROVED"); setNinFilterAnchorEl(null); }}>APPROVED</MenuItem>
                <MenuItem onClick={() => { setNinFilter("REJECTED"); setNinFilterAnchorEl(null); }}>REJECTED</MenuItem>
                <MenuItem onClick={() => { setNinFilter("ALL"); setNinFilterAnchorEl(null); }}>ALL</MenuItem>
              </Menu>
            </Box>
          </Box>
        )}
      </Box>

      {/* TABS CONTAINER */}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            borderRadius: 3,
            "& .MuiTabs-indicator": {
              backgroundColor: colors.orangeAccent[500],
            },
          }}
        >
          <Tab
            label="General KYC"
            sx={{
              bgcolor: colors.primary[400],
              mx: 0.5,
              borderRadius: "4px 4px 0 0",
              "&.Mui-selected": {
                color: colors.grey[100],
                bgcolor: colors.orangeAccent[700],
              },
            }}
          />
          <Tab
            label="NIN Verifications"
            sx={{
              bgcolor: colors.primary[400],
              mx: 0.5,
              borderRadius: "4px 4px 0 0",
              "&.Mui-selected": {
                color: colors.grey[100],
                bgcolor: colors.orangeAccent[700],
              },
            }}
          />
          <Tab
            label="Utility Bills"
            sx={{
              bgcolor: colors.primary[400],
              mx: 0.5,
              borderRadius: "4px 4px 0 0",
              "&.Mui-selected": {
                color: colors.grey[100],
                bgcolor: colors.orangeAccent[700],
              },
            }}
          />
          <Tab
            label="CAC Accounts"
            sx={{
              bgcolor: colors.primary[400],
              mx: 0.5,
              borderRadius: "4px 4px 0 0",
              "&.Mui-selected": {
                color: colors.grey[100],
                bgcolor: colors.orangeAccent[700],
              },
            }}
          />
        </Tabs>
      </Box>

      {/* DATAGRID GRID */}
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Box
          m="10px"
          width="100%"
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.orangeAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.redAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.redAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.orangeAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          {activeTab === 0 && (
            <DataGrid
              loading={generalLoading}
              getRowId={(row) => row.id}
              rows={generalKycData}
              columns={columnsGeneral}
            />
          )}

          {activeTab === 1 && (
            <DataGrid
              loading={ninLoading}
              getRowId={(row) => row.id}
              rows={filteredNinData}
              columns={columnsNIN}
            />
          )}

          {activeTab === 2 && (
            <DataGrid
              loading={utilityLoading}
              getRowId={(row) => row.id}
              rows={utilityKycData}
              columns={columnsUtility}
            />
          )}

          {activeTab === 3 && (
            <DataGrid
              loading={cacLoading}
              getRowId={(row) => row.id}
              rows={cacKycData}
              columns={columnsCAC}
            />
          )}
        </Box>
      </Grid>

      {/* NIN Comparison details modal */}
      <Modal open={ninModalOpen} onClose={() => setNinModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: smScreen ? "80%" : "95%",
            maxWidth: "900px",
            maxHeight: "90vh",
            bgcolor: colors.primary[400],
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflowY: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              NIN Identity Verification Details
            </Typography>
            <IconButton onClick={() => setNinModalOpen(false)} sx={{ color: colors.grey[100] }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {loadingNinDetails ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <>
              {/* Photo Comparison View */}
              <Box display="flex" flexDirection={smScreen ? "row" : "column"} gap={3} mb={3}>
                <Box flex={1}>
                  <Typography variant="h5" mb={1} color={colors.grey[300]} fontWeight="bold">
                    Official NIN Image (Verification Registry)
                  </Typography>
                  <Box
                    sx={{
                      height: "220px",
                      bgcolor: cardBg,
                      borderRadius: "6px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: `1px solid ${colors.grey[700]}`,
                      overflow: "hidden"
                    }}
                  >
                    {ninDetails?.verificationResult?.data?.photo ? (
                      <img
                        src={`data:image/jpeg;base64,${ninDetails.verificationResult.data.photo}`}
                        alt="Official Registry NIN"
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    ) : selectedNinRow?.ninPhotoUrl ? (
                      <img
                        src={selectedNinRow.ninPhotoUrl}
                        alt="Official Registry NIN"
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography color={colors.grey[300]}>No Registry Image Found</Typography>
                    )}
                  </Box>
                </Box>

                <Box flex={1}>
                  <Typography variant="h5" mb={1} color={colors.grey[300]} fontWeight="bold">
                    Captured Self Portrait
                  </Typography>
                  <Box
                    sx={{
                      height: "220px",
                      bgcolor: cardBg,
                      borderRadius: "6px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: `1px solid ${colors.grey[700]}`,
                      overflow: "hidden"
                    }}
                  >
                    {ninDetails?.capturedPhotoUrl ? (
                      <img
                        src={ninDetails.capturedPhotoUrl}
                        alt="Captured Selfie"
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    ) : selectedNinRow?.capturedPhotoUrl ? (
                      <img
                        src={selectedNinRow.capturedPhotoUrl}
                        alt="Captured Selfie"
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography color={colors.grey[300]}>No Captured Portrait Found</Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Data fields */}
              <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb={1.5}>
                Registry Information
              </Typography>
              <Grid container spacing={2} sx={{ bgcolor: cardBg, p: 2, borderRadius: "6px", mb: 3 }}>
                {[
                  { label: "Surname", value: ninDetails?.verificationResult?.data?.surname || selectedNinRow?.lastName },
                  { label: "First Name", value: ninDetails?.verificationResult?.data?.firstname || selectedNinRow?.firstName },
                  { label: "Middle Name", value: ninDetails?.verificationResult?.data?.middlename },
                  { label: "Date of Birth", value: ninDetails?.verificationResult?.data?.birthdate },
                  { label: "Gender", value: ninDetails?.verificationResult?.data?.gender },
                  { label: "Telephone Number", value: ninDetails?.verificationResult?.data?.telephoneno },
                  { label: "Residence Address", value: ninDetails?.verificationResult?.data?.residenceAddress }
                ].map((item, idx) => (
                  <Grid xs={6} md={4} key={idx}>
                    <Typography variant="body2" color={colors.grey[300]}>{item.label}</Typography>
                    <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                      {item.value || "N/A"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Remarks/Approval Action */}
              <Box mb={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks / Verification Notes"
                  variant="filled"
                  value={ninRemarks}
                  onChange={(e) => setNinRemarks(e.target.value)}
                  sx={{
                    bgcolor: cardBg,
                    "& .MuiInputBase-input": {
                      color: colors.grey[100],
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                    }
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="contained"
                  onClick={() => handleNinAction(false)}
                  sx={{
                    bgcolor: colors.redAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.redAccent[500] }
                  }}
                >
                  Reject Verification
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleNinAction(true)}
                  sx={{
                    bgcolor: colors.greenAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.greenAccent[500] }
                  }}
                >
                  Approve Verification
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Utility Bill details modal */}
      <Modal open={utilityModalOpen} onClose={() => setUtilityModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: smScreen ? "50%" : "95%",
            maxWidth: "600px",
            maxHeight: "90vh",
            bgcolor: colors.primary[400],
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflowY: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              Utility Bill Verification
            </Typography>
            <IconButton onClick={() => setUtilityModalOpen(false)} sx={{ color: colors.grey[100] }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedUtility && (
            <>
              {/* Document display */}
              <Box mb={3}>
                <Typography variant="h5" mb={1} color={colors.grey[300]} fontWeight="bold">
                  Uploaded Document
                </Typography>
                <Box
                  sx={{
                    height: "300px",
                    bgcolor: cardBg,
                    borderRadius: "6px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: `1px solid ${colors.grey[700]}`,
                    overflow: "hidden"
                  }}
                >
                  {selectedUtility.documentUrl ? (
                    selectedUtility.documentUrl.toLowerCase().endsWith(".pdf") ? (
                      <Button
                        href={selectedUtility.documentUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="contained"
                        sx={{ bgcolor: colors.orangeAccent[700], color: colors.grey[100] }}
                      >
                        Open PDF Bill
                      </Button>
                    ) : (
                      <img
                        src={selectedUtility.documentUrl}
                        alt="Utility Bill"
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    )
                  ) : (
                    <Typography color={colors.grey[300]}>No document uploaded</Typography>
                  )}
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>User ID</Typography>
                  <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                    {selectedUtility.userId}
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>Status</Typography>
                  <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                    {selectedUtility.status}
                  </Typography>
                </Grid>
              </Grid>

              {/* Remarks/Approval Action */}
              <Box mb={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks / Verification Notes"
                  variant="filled"
                  value={utilityRemarks}
                  onChange={(e) => setUtilityRemarks(e.target.value)}
                  sx={{
                    bgcolor: cardBg,
                    "& .MuiInputBase-input": {
                      color: colors.grey[100],
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                    }
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="contained"
                  onClick={() => handleUtilityAction(false)}
                  sx={{
                    bgcolor: colors.redAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.redAccent[500] }
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleUtilityAction(true)}
                  sx={{
                    bgcolor: colors.greenAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.greenAccent[500] }
                  }}
                >
                  Approve
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* CAC Account details modal */}
      <Modal open={cacModalOpen} onClose={() => setCacModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: smScreen ? "85%" : "95%",
            maxWidth: "950px",
            maxHeight: "90vh",
            bgcolor: colors.primary[400],
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            overflowY: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              CAC Business Onboarding Audit
            </Typography>
            <IconButton onClick={() => setCacModalOpen(false)} sx={{ color: colors.grey[100] }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedCac && (
            <>
              {/* Profile details */}
              <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb={1.5}>
                Business Information
              </Typography>
              <Grid container spacing={2} sx={{ bgcolor: cardBg, p: 2, borderRadius: "6px", mb: 3 }}>
                {[
                  { label: "Company Name", value: selectedCac.businessName },
                  { label: "RC Number", value: selectedCac.rcNumber },
                  { label: "Email Address", value: selectedCac.email },
                  { label: "Phone Number", value: selectedCac.phoneNo },
                  { label: "Registration Date", value: selectedCac.companyRegDate },
                  { label: "Commencement Date", value: selectedCac.businessCommencementDate },
                ].map((item, idx) => (
                  <Grid xs={6} md={4} key={idx}>
                    <Typography variant="body2" color={colors.grey[300]}>{item.label}</Typography>
                    <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                      {item.value || "N/A"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Directors & Shareholders */}
              <Box mb={3}>
                <Accordion sx={{ bgcolor: cardBg, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                      Directors List
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {selectedCac.directors && selectedCac.directors.length > 0 ? (
                      selectedCac.directors.map((director, idx) => (
                        <Box key={idx} sx={{ borderBottom: `1px solid ${colors.grey[700]}`, py: 1, mb: 1 }}>
                          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
                            {director.directorName || `Director ${idx + 1}`}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[300]}>
                            BVN: {director.directorBvn || "N/A"} | Phone: {director.directorPhoneNumber || "N/A"} | DOB: {director.directorDob || "N/A"}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color={colors.grey[300]}>No directors specified</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion sx={{ bgcolor: cardBg }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                      Shareholders List
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {selectedCac.shareholders && selectedCac.shareholders.length > 0 ? (
                      selectedCac.shareholders.map((shareholder, idx) => (
                        <Box key={idx} sx={{ borderBottom: `1px solid ${colors.grey[700]}`, py: 1, mb: 1 }}>
                          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
                            {shareholder.shareholderName || `Shareholder ${idx + 1}`}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[300]}>
                            BVN: {shareholder.shareholderBvn || "N/A"} | DOB: {shareholder.shareholderDob || "N/A"}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color={colors.grey[300]}>No shareholders specified</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Uploaded Documents */}
              <Box mb={3}>
                <Typography variant="h5" mb={1} color={colors.grey[100]} fontWeight="bold">
                  Uploaded Verification Documents
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: "CAC Document", url: selectedCac.cacDocumentUrl },
                    { label: "Utility Bill", url: selectedCac.utilityBillUrl },
                    { label: "Special Document", url: selectedCac.specialDocUrl }
                  ].map((doc, idx) => (
                    <Grid xs={12} sm={4} key={idx}>
                      <Card sx={{ bgcolor: cardBg, height: "100%" }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold" mb={1} color={colors.grey[100]}>
                            {doc.label}
                          </Typography>
                          {doc.url ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Box sx={{ height: "100px", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: theme.palette.mode === "dark" ? colors.primary[400] : "#f5f5f5" }}>
                                {doc.url.toLowerCase().endsWith(".pdf") ? (
                                  <Typography variant="caption" color={colors.grey[300]}>PDF Document</Typography>
                                ) : (
                                  <img src={doc.url} alt={doc.label} style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                                )}
                              </Box>
                              <Button
                                href={doc.url}
                                target="_blank"
                                rel="noreferrer"
                                variant="contained"
                                size="small"
                                sx={{ bgcolor: colors.orangeAccent[700], color: colors.grey[100] }}
                              >
                                View File
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="body2" color={colors.grey[300]}>Not Provided</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Actions & Remarks */}
              <Box mb={2}>
                <TextField
                  fullWidth
                  label="Approved By (Admin Email Address)"
                  variant="filled"
                  value={cacApprovedBy}
                  onChange={(e) => setCacApprovedBy(e.target.value)}
                  sx={{
                    bgcolor: cardBg,
                    "& .MuiInputBase-input": {
                      color: colors.grey[100],
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                    },
                    mb: 2
                  }}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks / Rejection Reason"
                  variant="filled"
                  value={cacRemarks}
                  onChange={(e) => setCacRemarks(e.target.value)}
                  sx={{
                    bgcolor: cardBg,
                    "& .MuiInputBase-input": {
                      color: colors.grey[100],
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.grey[300],
                    }
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="contained"
                  onClick={() => handleCacAction(false)}
                  sx={{
                    bgcolor: colors.redAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.redAccent[500] }
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleCacAction(true)}
                  sx={{
                    bgcolor: colors.greenAccent[600],
                    color: colors.grey[100],
                    "&:hover": { bgcolor: colors.greenAccent[500] }
                  }}
                >
                  Approve
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageKYC;
